import {
    Alert,
    Modal,
    Textarea,
    SpaceBetween,
    FormField,
    Select,
    Button,
} from '@amzn/awsui-components-console';
import { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useAddStudentsToClassroomRosterMutation } from '@/api/generated';
import { useGetClassroom, useUpdateClassroom } from '@/api/hooks';
import {
    EMAIL_REGEX,
    getDisabledCapacityInputHint,
    NEW_LINE_SPACE_REGEX,
} from '@/components/classForm/ClassForm.utils';
import messages_detail from '@/containers/classPages/ClassDetailPage/ClassDetailPage.messages';
import { useProvider } from '@/data/ProviderContext';
import { useGilmoreEvents } from '@/data/useGilmoreEvents';
import { parseError } from '@/utils/gonzo-error-parse-utils';
import { calculateReservedLicenses } from '@/utils/helpers';
import { useDecodedParams } from '@/utils/paramHooks';

import { messages } from './index.messages';
import { Footer } from './ModalFooter';
import { buildEmptyProvidersMap } from './utils';

import type { ProviderOption, ProvidersMap, UserList } from './types';
import type { NonCancelableCustomEvent, SelectProps } from '@amzn/awsui-components-console';

const removeDuplicates = (strs: string[]) => [...new Set(strs).values()];

const sanitizeCSVListInput = (value: string): UserList =>
    removeDuplicates(
        value
            .replace(/[\s\n,]+/g, ' ')
            .trim()
            .split(' '),
    ).map((email) => ({ email }));

type ValidationState = {
    studentRoster: UserList;
    capacityExceededBy: number;
    gilmoreLicense: boolean;
};
const defaultValidationState: ValidationState = {
    studentRoster: [],
    capacityExceededBy: 0,
    gilmoreLicense: false,
};

export const StudentRosterModal = () => {
    const { formatMessage } = useIntl();
    const { classroomId } = useDecodedParams();
    const { thereIsActiveEvent, mostRecent: mostRecentEvent } = useGilmoreEvents();
    const provider = useProvider();
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [providersMap, setProvidersMap] = useState<ProvidersMap>(() =>
        buildEmptyProvidersMap(provider),
    );
    const [validationErrors, validationErrorsSet] =
        useState<ValidationState>(defaultValidationState);

    const { data: classData, updateCache } = useGetClassroom();
    const classCapacityHasExceeded = validationErrors.capacityExceededBy > 0;
    const requiresSubProvider = Boolean(provider.requiresSubProvider);

    const providerOptions = requiresSubProvider
        ? provider.subProviderData.map((data: { providerName: string }) => ({
              label: data.providerName,
              value: data.providerName,
          }))
        : [{ label: provider.name, value: provider.name }];

    const [selectedProvider, setSelectedProvider] = useState<ProviderOption>(providerOptions[0]);
    const currentProvider = providersMap[selectedProvider.value];

    const handleCapacityError = useCallback(
        ({ users }: { users: UserList }) => {
            const classroomUsers = classData?.studentRoster?.classroomUsers!;
            let currentStudentCount = classData?.getClassroom.classroom.studentCount as number;
            let currentCapacity = classData?.getClassroom.classroom.classCapacity as number;
            let currentRoster = classroomUsers;
            if (requiresSubProvider) {
                const subProviderCapacityMap = Object.fromEntries(
                    (classData?.getClassroom.classroom.subProviderCapacity ?? [])
                        .filter((en) => !!en) // TODO: remove null | undefined values from the list in gql schema
                        .map((entry) => [
                            entry!.subProviderArn,
                            {
                                capacity: entry!.capacity,
                                studentCount: entry!.subProviderStudentCount,
                            },
                        ]),
                );
                currentCapacity = subProviderCapacityMap[currentProvider.arn].capacity;
                currentStudentCount = subProviderCapacityMap[currentProvider.arn].studentCount;
                currentRoster = classroomUsers
                    .filter((st) => !!st)
                    .filter((student) => student!.eKitLicensedProviderArn === currentProvider.arn);
            }
            const currentReservedLicenses = calculateReservedLicenses(
                currentRoster,
                currentStudentCount,
            );

            const exceededAmount = users.length + currentReservedLicenses - currentCapacity;

            if (exceededAmount > 0) {
                validationErrorsSet((prev) => ({
                    ...prev,
                    capacityExceededBy: exceededAmount,
                    studentRoster: users,
                }));
            }
        },
        [
            classData?.studentRoster?.classroomUsers,
            classData?.getClassroom.classroom.studentCount,
            classData?.getClassroom.classroom.classCapacity,
            classData?.getClassroom.classroom.subProviderCapacity,
            currentProvider.arn,
            requiresSubProvider,
        ],
    );

    const {
        mutate: callAddStudents,
        isLoading: isAddingStudents,
        isSuccess: addStudentsSucceeded,
    } = useAddStudentsToClassroomRosterMutation({
        onMutate: ({ input: { users } }) => {
            const invalidEmails = (users as UserList).filter(({ email }) => {
                return !EMAIL_REGEX.test(email) || NEW_LINE_SPACE_REGEX.test(email);
            });

            if (invalidEmails.length) {
                throw new Error('InvalidEmails', { cause: invalidEmails });
            }
            return { context: users };
        },
        onError: (error: Error | string, { input }) => {
            textareaRef.current?.focus();
            if (typeof error === 'object' && error.message === 'InvalidEmails') {
                validationErrorsSet((prev) => ({
                    ...prev,
                    studentRoster: error.cause as UserList,
                }));
            } else if (typeof error === 'string') {
                const { students } = parseError(JSON.parse(error));
                if (
                    students.some((err: { code: string; message: string }) =>
                        err.code.includes('ClassCapacity'),
                    )
                ) {
                    handleCapacityError({ users: input.users } as { users: UserList });
                }
            }
        },
        onSuccess: (res, { input: { users } }) => {
            setProvidersMap({
                ...providersMap,
                [selectedProvider.value]: {
                    ...providersMap[selectedProvider.value],
                    studentSubListValue: '',
                },
            });
            updateCache({ studentRosterAddition: users as UserList });
        },
    });

    const { mutate: callUpdateClassroom, isLoading: classIsUpdating } = useUpdateClassroom({
        onError: (error: string) => {
            const { errors } = JSON.parse(error);
            const { errorCodes } = errors[0];
            if (errorCodes[0].code === 'PropertyError:IncreaseInvalid') {
                validationErrorsSet((prev) => ({
                    ...prev,
                    capacityExceededBy: 0,
                    gilmoreLicense: true,
                }));
            }
        },
        onSuccess: () => {
            addStudentsToRosterHandler();
        },
    });
    const isAnyMutationRunning = classIsUpdating || isAddingStudents;

    const addStudentsToRosterHandler = () => {
        if (!classroomId) return;

        const studentListValueForThisProvider = currentProvider.studentSubListValue;

        callAddStudents({
            input: {
                classroomId,
                providerArn: classData!.getClassroom.classroom.providerArn,
                users: sanitizeCSVListInput(studentListValueForThisProvider),
                ...(requiresSubProvider && {
                    subProviderArn: currentProvider.arn,
                }),
            },
        });
    };

    const updateCapacityWithRoster = () => {
        const studentListValueForThisProvider =
            providersMap[selectedProvider.value].studentSubListValue;

        const users = sanitizeCSVListInput(studentListValueForThisProvider);
        const licensesReserved = calculateReservedLicenses(
            classData?.studentRoster?.classroomUsers!,
            classData?.getClassroom.classroom.studentCount as number,
        );
        const newClassCapacity = licensesReserved + users.length;
        callUpdateClassroom({ classCapacity: newClassCapacity });
    };

    const roster = (classData?.studentRoster?.classroomUsers ?? []).filter(
        (student) => student !== null,
    );
    const availableCapacity = useMemo(() => {
        if (!currentProvider) return 0;

        const rosterForProvider = requiresSubProvider
            ? roster.filter((student) => student?.eKitLicensedProviderArn === currentProvider.arn)
            : roster;
        const reserved = calculateReservedLicenses(rosterForProvider, currentProvider.studentCount);

        return Math.max(0, currentProvider.capacity - reserved);
    }, [currentProvider, roster, requiresSubProvider]);

    const hasCapacity = availableCapacity > 0;

    const resetOverCapacityAndLicenseError = () => {
        validationErrorsSet(defaultValidationState);
    };

    const handleCloseModal = () => setIsOpen(false);

    useEffect(() => {
        if (!classData?.getClassroom.classroom || !provider) return;
        const { classCapacity, studentCount, subProviderCapacity } =
            classData.getClassroom.classroom;

        const currentProviderMap = buildEmptyProvidersMap(provider);

        if (!requiresSubProvider) {
            setProvidersMap({
                ...currentProviderMap,
                [provider?.name]: {
                    ...currentProviderMap[provider?.name],
                    capacity: classCapacity,
                    studentCount: studentCount,
                },
            });
        } else if (subProviderCapacity) {
            const subProviderCapacityMap = Object.fromEntries(
                subProviderCapacity.map((entry: any) => [
                    entry.subProviderArn,
                    {
                        capacity: entry.capacity,
                        studentCount: entry.subProviderStudentCount,
                    },
                ]),
            );

            setProvidersMap(
                Object.fromEntries(
                    Object.keys(currentProviderMap).map((providerName) => {
                        const capacityDataOfThisProvider =
                            subProviderCapacityMap[currentProviderMap[providerName].arn];
                        return [
                            providerName,
                            {
                                ...currentProviderMap[providerName],
                                capacity: capacityDataOfThisProvider?.capacity ?? 0,
                                studentCount: capacityDataOfThisProvider?.studentCount ?? 0,
                            },
                        ];
                    }),
                ),
            );
        }
    }, [classData, provider, requiresSubProvider]);

    const handleProviderChange = (event: NonCancelableCustomEvent<SelectProps.ChangeDetail>) => {
        const selectedOption = event.detail.selectedOption as ProviderOption;
        setSelectedProvider(selectedOption);
        validationErrorsSet(defaultValidationState);
    };

    const handleTextareaChange = ({ detail }: { detail: { value: string } }) => {
        if (validationErrors.studentRoster.length) {
            validationErrorsSet((cur) => ({ ...cur, studentRoster: [] }));
        }
        setProvidersMap({
            ...providersMap,
            [selectedProvider.value]: {
                ...providersMap[selectedProvider.value],
                studentSubListValue: detail.value,
            },
        });
    };

    if (!classroomId) return null;

    return (
        <>
            <Button data-testid='show-add-student-modal' onClick={() => setIsOpen(true)}>
                {formatMessage(messages_detail.addStudentListButton)}
            </Button>
            <Modal
                onDismiss={handleCloseModal}
                closeAriaLabel={formatMessage(messages.closeModalButtonLabel)}
                data-testid='student-roster-modal'
                visible={isOpen}
                header={formatMessage(messages.headerLabel)}
                footer={
                    <Footer
                        requiresSubProvider={!!requiresSubProvider}
                        onCloseStudentListModal={handleCloseModal}
                        addStudentsToRosterHandler={addStudentsToRosterHandler}
                        classCapacitySizeExceededBy={validationErrors.capacityExceededBy}
                        capacityUpdateWithRoster={updateCapacityWithRoster}
                        gilmoreLicenseError={validationErrors.gilmoreLicense}
                        thereIsActiveEvent={thereIsActiveEvent}
                        showBtnSpinner={isAnyMutationRunning}
                        resetOverCapacityAndLicenseError={resetOverCapacityAndLicenseError}
                        formatMessage={formatMessage}
                        classroomId={classroomId}
                        providersMap={providersMap}
                        classCapacityHasExceeded={classCapacityHasExceeded}
                    />
                }
            >
                <SpaceBetween direction='vertical' size='s' data-testid='asdfsdf'>
                    {classCapacityHasExceeded ? (
                        <Alert type='error'>
                            {formatMessage(
                                requiresSubProvider
                                    ? messages.classCapacityExceededMessageWithSubProvider
                                    : messages.classCapacityExceededMessage,
                                {
                                    size: validationErrors.capacityExceededBy,
                                },
                            )}
                        </Alert>
                    ) : (
                        <Alert>
                            {availableCapacity === 0 &&
                                formatMessage(messages.classCapacityNoUnassignedSeats)}
                            {availableCapacity === 1 &&
                                formatMessage(messages.classCapacitySingleUnassignedSeats)}
                            {availableCapacity > 1 &&
                                formatMessage(messages.classCapacityMultipleUnassignedSeats, {
                                    num: availableCapacity,
                                })}{' '}
                            <span>
                                {getDisabledCapacityInputHint(mostRecentEvent, formatMessage, {
                                    plannedMessage: messages.classCapacityPlannedOutageDisabled,
                                    unplannedMessage: messages.classCapacityUnplannedOutageDisabled,
                                })}
                            </span>
                        </Alert>
                    )}

                    {!classCapacityHasExceeded && !hasCapacity ? (
                        <Alert type='warning'>
                            {formatMessage(
                                requiresSubProvider
                                    ? messages.classLimitBodyWithSubProvider
                                    : messages.classLimitBody,
                            )}
                        </Alert>
                    ) : null}

                    {validationErrors.gilmoreLicense ? (
                        <Alert type='error'>{formatMessage(messages.gilmoreLicenseError)}</Alert>
                    ) : null}

                    {classData?.getClassroom.classroom?.learningActivityID && hasCapacity && (
                        <Alert type='warning'>
                            {formatMessage(messages.rosterDiscrepancyWarning)}
                        </Alert>
                    )}

                    {addStudentsSucceeded ? (
                        <Alert type='success'>
                            {formatMessage(messages.addStudentsToRosterSucceed)}
                        </Alert>
                    ) : null}

                    <FormField
                        data-testid='asdf'
                        label={formatMessage(messages.bodyTitle)}
                        description={formatMessage(messages.bodyInstructions)}
                        errorText={
                            validationErrors.studentRoster.length
                                ? validationErrors.studentRoster.map((error, i) => (
                                      <p key={`${error.email}-${i}`}>
                                          {error.email} {formatMessage(messages.invalidEmail)}
                                      </p>
                                  ))
                                : undefined
                        }
                        constraintText={formatMessage(
                            requiresSubProvider
                                ? messages.classCapacityBodySubProvider
                                : messages.classCapacityBody,
                            { classCapacity: availableCapacity },
                        )}
                    >
                        <SpaceBetween direction='vertical' size='m'>
                            <Select
                                data-testid='sub-provider-select'
                                selectedOption={selectedProvider}
                                onChange={handleProviderChange}
                                options={providerOptions}
                                disabled={!requiresSubProvider}
                            />

                            <Textarea
                                onChange={handleTextareaChange}
                                ref={textareaRef}
                                value={providersMap[selectedProvider.value].studentSubListValue}
                                ariaLabel='student-emails'
                                placeholder={formatMessage(messages.placeholder)}
                                disabled={
                                    classCapacityHasExceeded || validationErrors.gilmoreLicense
                                }
                            />
                        </SpaceBetween>
                    </FormField>
                </SpaceBetween>
            </Modal>
        </>
    );
};

export default StudentRosterModal;
